import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Tab,
  Tabs,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Link,
  Divider,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { Link as RouterLink } from 'react-router-dom';
import LegalModal from './LegalModal';
import { apiService } from '../services/api';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Login = () => {
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // Collected as two fields, stored as one joined `name` — see personName.js.
  // The Dashboard greeting, header chip and avatar initials all read `name`,
  // so the split is real at the edges and joined in storage.
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  // Optional at signup. Anyone who skips it is still caught by the billing
  // gate before their first purchase, so this is a convenience, not a control.
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, type: 'terms' });

  const { signIn, signUp, resendConfirmation, signInWithGoogle, userDataError, clearUserDataError } = useAuth();

  useEffect(() => {
    if (userDataError) {
      setError(userDataError);
      clearUserDataError();
    }
  }, [userDataError, clearUserDataError]);

  const handleSignInWithGoogle = async () => {
    try {
      setLoading(true);
      setError('');
      const { error } = await signInWithGoogle();
      if (error) throw error;
    } catch (error) {
      setError(error.message || 'Failed to sign in with Google');
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      if (tabValue === 0) {
        // Sign In — check if user is Google-only before attempting password login
        const eligibility = await apiService.verifyResetEligibility(email);
        if (eligibility.success && !eligibility.eligible && eligibility.reason === 'oauth_only') {
          setError(`This account uses ${eligibility.provider?.charAt(0).toUpperCase() + eligibility.provider?.slice(1)} sign-in. Please use the "Sign in with Google" button below.`);
          setLoading(false);
          return;
        }

        const { error } = await signIn(email, password);
        if (error) throw error;
      } else {
        // Sign Up
        //
        const { error } = await signUp(email, password, {
          first: firstName,
          last: lastName,
        });
        if (error) throw error;
        setMessage('Please check your email for the confirmation link!');
      }
    } catch (error) {
      setError(error.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    try {
      setLoading(true);
      const { error } = await resendConfirmation(email);
      if (error) throw error;
      setMessage('Confirmation email sent!');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 480 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 1 }}>
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', overflow: 'hidden', flexShrink: 0 }}>
                  <img src="/improvelogo.png" alt="ImproveSEO" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                </Box>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 800, fontFamily: 'Manrope, sans-serif' }}>
                  ImproveSEO
                </Typography>
              </Box>
              <Typography variant="body1" color="text.secondary">
                User Management Portal
              </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange} centered>
                <Tab label="Sign In" />
                <Tab label="Sign Up" />
              </Tabs>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {message && (
              <Alert 
                severity="success" 
                sx={{ mb: 2 }}
                action={
                  message.includes('confirmation') && (
                    <Button 
                      color="inherit" 
                      size="small" 
                      onClick={handleResendConfirmation}
                      disabled={loading}
                    >
                      Resend
                    </Button>
                  )
                }
              >
                {message}
                {/* The Resend action above already existed and already fires
                    for this message — what was missing is telling the user it
                    is there, and that the mail is very often simply in spam.
                    Without this line the alert announces a problem and offers
                    no next step. */}
                {message.includes('confirmation link') && (
                  <Typography variant="body2" sx={{ mt: 0.75 }}>
                    Didn't receive the email? Check your spam folder, or click{' '}
                    <Link
                      component="button"
                      type="button"
                      onClick={handleResendConfirmation}
                      disabled={loading}
                      sx={{ verticalAlign: 'baseline', fontWeight: 600 }}
                    >
                      Resend
                    </Link>.
                  </Typography>
                )}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TabPanel value={tabValue} index={0}>
                {/* Sign In Form */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(v => !v)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{ mt: 2 }}
                  >
                    {loading ? 'Signing In...' : 'Sign In'}
                  </Button>

                  <Box sx={{ textAlign: 'right' }}>
                    <Link component={RouterLink} to="/forgot-password" underline="hover" variant="body2">
                      Forgot password?
                    </Link>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="body2" color="text.secondary">OR</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>

                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={handleSignInWithGoogle}
                    disabled={loading}
                    startIcon={
                      loading ? <CircularProgress size={18} /> : (
                        <img
                          src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                          alt="Google"
                          style={{ width: 20, height: 20 }}
                        />
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      fontSize: '1rem',
                      py: 1.5,
                      borderColor: '#D3D3D3',
                      color: 'text.primary',
                      '&:hover': { backgroundColor: '#f9f9f9', borderColor: '#aaa' },
                    }}
                  >
                    Sign in with Google
                  </Button>
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                {/* Sign Up Form */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Two fields, not one "Full Name". Splitting a name after
                      the fact needs a heuristic that is wrong for a great many
                      real names — asking is the only reliable way to know which
                      part is which. Side by side on a tablet, stacked on a
                      phone. */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      autoComplete="given-name"
                      inputProps={{ maxLength: 100 }}
                    />
                    <TextField
                      fullWidth
                      label="Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      autoComplete="family-name"
                      inputProps={{ maxLength: 100 }}
                    />
                  </Box>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    helperText="Minimum 8 characters"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(v => !v)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={agreedToTerms}
                        onChange={(e) => setAgreedToTerms(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2">
                        I agree to the{' '}
                        <Link component="button" type="button" onClick={() => setLegalModal({ open: true, type: 'terms' })}>Terms of Service</Link>
                        {' '}and{' '}
                        <Link component="button" type="button" onClick={() => setLegalModal({ open: true, type: 'privacy' })}>Privacy Policy</Link>
                      </Typography>
                    }
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={loading || !agreedToTerms}
                    startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}
                    sx={{ mt: 2 }}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </Button>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
                    <Divider sx={{ flex: 1 }} />
                    <Typography variant="body2" color="text.secondary">OR</Typography>
                    <Divider sx={{ flex: 1 }} />
                  </Box>

                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={handleSignInWithGoogle}
                    disabled={loading}
                    startIcon={
                      loading ? <CircularProgress size={18} /> : (
                        <img
                          src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                          alt="Google"
                          style={{ width: 20, height: 20 }}
                        />
                      )
                    }
                    sx={{
                      textTransform: 'none',
                      fontSize: '1rem',
                      py: 1.5,
                      borderColor: '#D3D3D3',
                      color: 'text.primary',
                      '&:hover': { backgroundColor: '#f9f9f9', borderColor: '#aaa' },
                    }}
                  >
                    Sign up with Google
                  </Button>
                </Box>
              </TabPanel>
            </form>

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                By signing up, you agree to our{' '}
                <Link component="button" type="button" onClick={() => setLegalModal({ open: true, type: 'terms' })}>Terms of Service</Link>
                {' '}and{' '}
                <Link component="button" type="button" onClick={() => setLegalModal({ open: true, type: 'privacy' })}>Privacy Policy</Link>
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Box>
      <LegalModal
        open={legalModal.open}
        onClose={() => setLegalModal({ open: false, type: legalModal.type })}
        type={legalModal.type}
      />
    </Container>
  );
};

export default Login;
